import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { getChatApiUrl } from '../../utils/chatApi';
import { useGeolocation } from '../../hooks/useGeolocation';

const ChatWidget = () => {
  const navigate = useNavigate();
  const { location } = useGeolocation();
  const [isOpen, setIsOpen] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! Soy la asistente de **E-MOREIRA**. ¿En qué puedo ayudarte hoy?',
      buttons: [
        { text: 'Ver categorías', action: 'categories', icon: 'fa-tags' },
        { text: 'Buscar productos', action: 'search', icon: 'fa-search' },
        { text: 'Mis pedidos', action: 'orders', icon: 'fa-box' }
      ]
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const checkChatStatus = async () => {
      try {
        const response = await axios.get(getChatApiUrl('health'));
        setChatEnabled(response.data.chatEnabled === true);
      } catch (error) {
        console.error('Error checking chat status:', error);
        setChatEnabled(false);
      } finally {
        setWidgetLoaded(true);
      }
    };
    checkChatStatus();
  }, []);

  const sendMessage = async (e, buttonAction = null) => {
    if (e) e.preventDefault();
    if (!buttonAction && (!input.trim() || isLoading)) return;

    const userMessage = buttonAction
      ? { role: 'user', content: `[Acción: ${buttonAction.text}]`, isButton: true }
      : { role: 'user', content: input.trim() };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const requestBody = buttonAction
        ? {
          buttonAction: { action: buttonAction.action, data: buttonAction.data || {} },
          location: location // Incluir ubicación si está disponible
        }
        : {
          messages: newMessages,
          location: location // Incluir ubicación si está disponible
        };

      const response = await axios.post(getChatApiUrl(), requestBody);

      const assistantMessage = {
        role: 'assistant',
        content: response.data.reply || 'Lo siento, no pude procesar tu consulta.',
        buttons: response.data.buttons || [],
        products: response.data.products || []
      };

      setMessages([...newMessages, assistantMessage]);
    } catch (error) {
      console.error('Error en chat:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
        buttons: [{ text: '🏠 Volver al inicio', action: 'home', icon: 'fa-home' }]
      };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => {
    // Regex para detectar imágenes en formato markdown ![alt](url|id)
    const markdownImageRegex = /!\[([^\]]*)\]\(([^\|\)]+)(?:\|([^\)]+))?\)/g;
    // Regex para URLs directas de imágenes
    const directImageRegex = /(https?:\/\/[^\s\)]+\.(jpg|jpeg|png|gif|webp|svg)[^\s\)]*)/gi;
    // Regex para enlaces clickeables
    const linkRegex = /(https?:\/\/[^\s]+)/gi;

    // Dividir el contenido en líneas para procesamiento
    const lines = content.split('\n');
    const processedLines = [];

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      let hasProcessedContent = false;

      // 1. Procesar imágenes en formato markdown ![alt](url)
      const markdownMatches = [...line.matchAll(markdownImageRegex)];
      if (markdownMatches.length > 0) {
        let lastIndex = 0;

        markdownMatches.forEach((match, imgIndex) => {
          const [fullMatch, altText, imageUrl, productId] = match;
          const matchStart = match.index;
          const resolvedImageUrl = getDisplayImageUrl(imageUrl);

          // Agregar texto antes de la imagen
          if (matchStart > lastIndex) {
            const beforeText = line.substring(lastIndex, matchStart);
            if (beforeText.trim()) {
              processedLines.push(
                <div key={`text-${i}-${imgIndex}-before`} dangerouslySetInnerHTML={{
                  __html: beforeText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }} />
              );
            }
          }

          // Agregar la imagen con botones
          processedLines.push(
            <div key={`img-${i}-${imgIndex}`} className="product-card-chat">
              <img
                src={resolvedImageUrl}
                alt={altText || "Imagen del producto"}
                className="product-image-chat"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="image-error" style={{ display: 'none' }}>
                📷 Imagen no disponible
              </div>
              <div className="product-actions mt-2 d-flex flex-wrap gap-1">
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: '#1a1a1a', color: 'white', borderRadius: '4px', fontSize: '0.8rem', padding: '4px 8px', border: 'none' }}
                  onClick={() => {
                    if (productId && productId.trim()) {
                      const url = `/product/${productId.trim()}`;
                      navigate(url);
                    } else {
                      const productName = altText || 'producto';
                      setInput(`buscar ${productName}`);
                    }
                  }}
                >
                  <i className="fas fa-shopping-bag me-1"></i> Ver producto
                </button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: 'transparent', color: '#1a1a1a', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.8rem', padding: '4px 8px' }}
                  onClick={() => {
                    const productName = altText || 'producto';
                    setInput(`más información sobre ${productName}`);
                  }}
                >
                  <i className="fas fa-info-circle me-1"></i> Info
                </button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: 'transparent', color: '#6c757d', border: 'none', fontSize: '0.8rem', padding: '4px 8px' }}
                  onClick={() => window.open(resolvedImageUrl, '_blank', 'noopener,noreferrer')}
                >
                  <i className="fas fa-image"></i>
                </button>
              </div>
            </div>
          );

          lastIndex = matchStart + fullMatch.length;
          hasProcessedContent = true;
        });

        // Agregar texto después de todas las imágenes
        if (lastIndex < line.length) {
          const afterText = line.substring(lastIndex);
          if (afterText.trim()) {
            processedLines.push(
              <div key={`text-${i}-after`} dangerouslySetInnerHTML={{
                __html: afterText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              }} />
            );
          }
        }
      }

      // 2. Si no hay imágenes markdown, buscar URLs directas de imágenes
      else {
        directImageRegex.lastIndex = 0;
      }

      if (!hasProcessedContent && directImageRegex.test(line)) {
        directImageRegex.lastIndex = 0;
        const imageMatches = line.match(directImageRegex);
        let remainingText = line;

        imageMatches.forEach((imageUrl, imgIndex) => {
          const cleanUrl = imageUrl.replace(/[\)\]]+$/, '');
          const resolvedImageUrl = getDisplayImageUrl(cleanUrl);
          const parts = remainingText.split(imageUrl);

          if (parts[0] && parts[0].trim()) {
            processedLines.push(
              <div key={`text-${i}-${imgIndex}-before`} dangerouslySetInnerHTML={{
                __html: parts[0].replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              }} />
            );
          }

          processedLines.push(
            <div key={`img-${i}-${imgIndex}`} className="product-card-chat">
              <img
                src={resolvedImageUrl}
                alt="Imagen del producto"
                className="product-image-chat"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="image-error" style={{ display: 'none' }}>
                📷 Imagen no disponible
              </div>
              <div className="product-actions mt-2 d-flex flex-wrap gap-1">
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: '#1a1a1a', color: 'white', borderRadius: '4px', fontSize: '0.8rem', padding: '4px 8px', border: 'none' }}
                  onClick={() => {
                    navigate('/tienda');
                  }}
                >
                  <i className="fas fa-shopping-bag me-1"></i> Ver producto
                </button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: 'transparent', color: '#1a1a1a', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.8rem', padding: '4px 8px' }}
                  onClick={() => {
                    setInput('más información sobre este producto');
                  }}
                >
                  <i className="fas fa-info-circle me-1"></i> Info
                </button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: 'transparent', color: '#6c757d', border: 'none', fontSize: '0.8rem', padding: '4px 8px' }}
                  onClick={() => window.open(resolvedImageUrl, '_blank', 'noopener,noreferrer')}
                >
                  <i className="fas fa-image"></i>
                </button>
              </div>
            </div>
          );

          remainingText = parts.slice(1).join(imageUrl);
        });

        if (remainingText && remainingText.trim()) {
          processedLines.push(
            <div key={`text-${i}-after`} dangerouslySetInnerHTML={{
              __html: remainingText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            }} />
          );
        }
        hasProcessedContent = true;
      }

      // 3. Si no hay imágenes, procesar enlaces clickeables y texto normal
      if (!hasProcessedContent) {
        // Detectar enlaces y hacerlos clickeables
        linkRegex.lastIndex = 0;
        if (linkRegex.test(line)) {
          linkRegex.lastIndex = 0;
          const parts = line.split(linkRegex);
          const elements = [];

          for (let j = 0; j < parts.length; j++) {
            if (j % 2 === 0) {
              // Texto normal
              if (parts[j]) {
                const processedText = parts[j]
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\*(.*?)\*/g, '<em>$1</em>')
                  .replace(/_(.*?)_/g, '<em>$1</em>')
                  .replace(/~~(.*?)~~/g, '<del>$1</del>');
                elements.push(<span key={`text-${j}`} dangerouslySetInnerHTML={{ __html: processedText }} />);
              }
            } else {
              // Enlace clickeable
              elements.push(
                <button
                  key={`link-${j}`}
                  className="btn btn-sm btn-link p-0 mx-1"
                  onClick={() => window.open(parts[j], '_blank')}
                  style={{ textDecoration: 'underline', color: 'var(--brand-primary, #c9a96e)' }}
                >
                  <i className="fas fa-external-link-alt me-1"></i>
                  {parts[j].length > 30 ? parts[j].substring(0, 30) + '...' : parts[j]}
                </button>
              );
            }
          }

          processedLines.push(
            <div key={`text-${i}`}>{elements}</div>
          );
        } else {
          // Texto normal sin enlaces
          const processedLine = line
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/_(.*?)_/g, '<em>$1</em>')
            .replace(/~~(.*?)~~/g, '<del>$1</del>');

          if (processedLine.trim()) {
            processedLines.push(
              <div key={`text-${i}`} dangerouslySetInnerHTML={{ __html: processedLine }} />
            );
          }
        }
      }
    }

    return processedLines;
  };

  const handleButtonClick = (button) => {
    sendMessage(null, button);
  };

  if (!widgetLoaded || !chatEnabled) {
    return null;
  }

  return (
    <div className={`chat-widget ${isOpen ? 'open' : 'closed'}`}>
      {/* Botón flotante */}
      {!isOpen && (
        <button
          className="chat-toggle"
          onClick={() => setIsOpen(true)}
          aria-label="Abrir chat"
        >
          <i className="fas fa-comments"></i>
        </button>
      )}

      {/* Ventana de chat */}
      {isOpen && (
        <div className="chat-container">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-avatar">
                <i className="fa-brands fa-reddit-alien"></i>
              </div>
              <div>
                <h6 className="mb-0 text-white" style={{ fontWeight: '600' }}>Asistente L'ETÉ</h6>
                <small style={{ color: 'rgba(255, 255, 255, 0.8)' }}>En línea</small>
              </div>
            </div>
            <button
              className="btn btn-sm btn-outline-light"
              onClick={() => setIsOpen(false)}
            >
              <i className="fas fa-minus"></i>
            </button>
          </div>

          {/* Mensajes */}
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <div className="message-content">
                  {!message.isButton && formatMessage(message.content)}
                  {message.isButton && (
                    <div style={{ opacity: 0.7, fontStyle: 'italic', fontSize: '0.9em' }}>
                      {message.content}
                    </div>
                  )}
                </div>

                {/* Botones de acción */}
                {message.buttons && message.buttons.length > 0 && (
                  <div className="chat-action-buttons">
                    {message.buttons.map((button, btnIndex) => (
                      <button
                        key={btnIndex}
                        className="btn btn-sm chat-btn"
                        style={{ 
                          border: '1px solid #c9a96e', 
                          color: '#1a1a1a', 
                          backgroundColor: 'transparent', 
                          borderRadius: '20px', 
                          padding: '6px 14px', 
                          margin: '4px 4px 4px 0', 
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                        onClick={() => handleButtonClick(button)}
                        disabled={isLoading}
                      >
                        {button.icon && <i className={`fas ${button.icon} me-1`}></i>}
                        {button.text}
                      </button>
                    ))}
                  </div>
                )}

                <div className="message-time">
                  {new Date().toLocaleTimeString('es-UY', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="message assistant">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>


          {/* Input */}
          {/* Input */}
          <form className="chat-input-form" onSubmit={sendMessage} style={{ borderTop: '1px solid #dee2e6', padding: '12px 16px', backgroundColor: '#fff', borderRadius: '0 0 16px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f8f9fa', borderRadius: '24px', padding: '4px 8px', border: '1px solid #e9ecef' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Escribe tu mensaje..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={isLoading}
                style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none', flexGrow: 1, padding: '8px 12px', fontSize: '0.9rem' }}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                style={{ 
                  backgroundColor: input.trim() ? '#1a1a1a' : '#e9ecef', 
                  color: input.trim() ? '#fff' : '#adb5bd', 
                  borderRadius: '50%', 
                  width: '36px', 
                  height: '36px', 
                  minWidth: '36px',
                  maxWidth: '36px',
                  flexShrink: 0,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: 0,
                  border: 'none',
                  transition: 'all 0.2s',
                  marginLeft: '8px',
                  cursor: (!input.trim() || isLoading) ? 'not-allowed' : 'pointer'
                }}
              >
                {isLoading ? (
                  <div className="spinner-border spinner-border-sm text-light" role="status" style={{ width: '1rem', height: '1rem' }}>
                    <span className="visually-hidden">Enviando...</span>
                  </div>
                ) : (
                  <i className="fas fa-paper-plane" style={{ fontSize: '0.8rem', marginLeft: '-2px' }}></i>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
